import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useTranslation } from "react-i18next";
import BookingGetters from "../../store/onlineBooking/getters";
import { ShareboxPickupResponse } from "../../@types/DTO/Sharebox";
import ShareboxHeader from "./components/ShareboxHeader";
import ShareboxFooter from "./components/ShareboxFooter";

type Props = {
  pickup: ShareboxPickupResponse;
  onNext: () => void;
  onBack?: () => void;
};

const COLUMNS = 8;
const FALLBACK_TOTAL_BOXES = 32;

export default function D9Success({ pickup, onNext, onBack }: Props) {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;
  const { cabinet, boxNumber, totalBoxesInCabinet, plate } = pickup.booking;

  const totalBoxes = totalBoxesInCabinet || FALLBACK_TOTAL_BOXES;
  const highlightedIndex = (Number(boxNumber) || 1) - 1; // shareboxLockerId is 1-based
  const boxLabel = `BOX-${boxNumber}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <ShareboxHeader
        logoUrl={appSettings?.config?.logoUrl}
        rightSlot={`${translate("Step")} 3 ${translate("of")} 3`}
        onBack={onBack}
      />

      <Box sx={{ maxWidth: 480, mx: "auto", px: 3, py: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
            border: "1px solid",
            borderColor: "success.main",
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <CheckCircleIcon sx={{ color: "success.main", mt: "2px" }} />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>
              {translate("Box")} {boxNumber} {translate("is open")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate("Grab the key tagged")} {plate}.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid",
              borderColor: webCheckinSecondaryColor || "#E0007A",
              borderRadius: 2,
            }}
          >
            <VpnKeyIcon
              sx={{
                color: webCheckinSecondaryColor || "#E0007A",
                fontSize: 28,
              }}
            />
          </Box>
        </Box>

        <Typography variant="h6" align="center" sx={{ fontWeight: 700, mb: 2 }}>
          {translate("Find your car")}
        </Typography>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            p: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ fontWeight: 700, mb: 2 }}
          >
            {cabinet} · {boxLabel}
          </Typography>
          <Box
            data-testid="parking-grid"
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${COLUMNS}, 1fr)`,
              gap: 1,
            }}
          >
            {Array.from({ length: totalBoxes }).map((_, i) => {
              const isHighlighted = i === highlightedIndex;
              return (
                <Box
                  key={i}
                  sx={{
                    aspectRatio: "1",
                    borderRadius: 1,
                    backgroundColor: isHighlighted
                      ? webCheckinSecondaryColor || "#E0007A"
                      : "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isHighlighted && (
                    <Typography
                      sx={{ color: "#fff", fontWeight: 700, fontSize: 9 }}
                    >
                      {boxLabel}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate(
            "Exit Terminal 1 and follow the signs for Rental to find your cabinet."
          )}
        </Typography>

        <Button
          variant="contained"
          fullWidth
          className="webCheckInButton"
          onClick={onNext}
          sx={{
            width: "100% !important",
            maxWidth: "none !important",
            color: "#fff",
          }}
        >
          {translate("Done — safe travels!")}
        </Button>
      </Box>
      <ShareboxFooter />
    </Box>
  );
}
